import { surfaces } from '@/theme/tokens';

import { firstGrapheme, initials } from './initials';
import { MAX_SIGNATURE_DATA_URL_LENGTH } from './signatureImage';

/**
 * How the director's name is rendered into a typed signature. These are the
 * three styles the old portal offered, kept because they map to how people
 * actually sign: in full, with initials for given names, or initials only.
 */
export const NAME_STYLES = {
  fullName: 'Full name',
  initialLastName: 'Initial and last name',
  initialsOnly: 'Initials only',
} as const;

export type NameStyle = keyof typeof NAME_STYLES;

/** Fonts are imported in `src/styles/index.css`; the stack falls back to cursive
 *  so a failed font load still renders something signature-shaped. */
export const SIGNATURE_FONTS = {
  greatVibes: { label: 'Flowing', stack: "'Great Vibes', cursive" },
  dancingScript: { label: 'Classic', stack: "'Dancing Script', cursive" },
  caveat: { label: 'Casual', stack: "'Caveat', cursive" },
} as const;

export type SignatureFont = keyof typeof SIGNATURE_FONTS;

/**
 * Applies a name style to a director's full name.
 *
 * Everything here is derived from the one `fullName` the API gives us, since
 * ASIC's three given-name fields are already joined by `fullNameOf`.
 */
export const applyNameStyle = (fullName: string, style: NameStyle): string => {
  const words = fullName.trim().split(/\s+/).filter(Boolean);
  const first = words[0];
  if (first === undefined) return '';

  switch (style) {
    case 'fullName':
      return words.join(' ');

    case 'initialLastName': {
      // With a single word there is no surname to keep, so the name is already
      // as short as this style can make it.
      if (words.length === 1) return first;

      const last = words[words.length - 1] ?? '';
      const leading = words
        .slice(0, -1)
        .map((word) => `${firstGrapheme(word)}.`)
        .join(' ');

      return `${leading} ${last}`;
    }

    case 'initialsOnly':
      return initials(fullName);
  }
};

/** Canvas size for a typed signature. Fixed rather than measured so every saved
 *  signature has the same aspect ratio however long the name is. */
const WIDTH = 900;
const HEIGHT = 300;
const MAX_FONT_SIZE = 130;
const MIN_FONT_SIZE = 28;
/** Leaves the glyphs clear of the edge — script faces overhang their metrics. */
const HORIZONTAL_PADDING = 60;

/**
 * Rasterises a typed signature to a PNG data URL.
 *
 * The font size is chosen by measuring rather than by a lookup table keyed on
 * name length, which is what the old portal did — that table guessed, and a
 * long name in a wide face still overflowed its box.
 *
 * Returns `null` when there is nothing to draw or no 2D context, so the caller
 * treats it the same as an empty drawing.
 */
export const renderTypedSignature = (text: string, font: SignatureFont): string | null => {
  const trimmed = text.trim();
  if (trimmed === '') return null;

  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;

  const ctx = canvas.getContext('2d');
  if (ctx === null) return null;

  const stack = SIGNATURE_FONTS[font].stack;
  const available = WIDTH - HORIZONTAL_PADDING * 2;

  let size = MAX_FONT_SIZE;
  ctx.font = `${String(size)}px ${stack}`;

  // Shrink until it fits. Stepping down beats solving for the ratio in one go
  // because a script face's advance width is not linear in font size.
  while (ctx.measureText(trimmed).width > available && size > MIN_FONT_SIZE) {
    size -= 2;
    ctx.font = `${String(size)}px ${stack}`;
  }

  // Canvas cannot read CSS variables, so the ink comes from the token itself.
  ctx.fillStyle = surfaces.text.primary;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(trimmed, WIDTH / 2, HEIGHT / 2, available);

  const dataUrl = canvas.toDataURL('image/png');

  // Typed signatures are flat text and compress well, so this should never
  // trip — but the storage column is a hard limit and a silent 500 at save
  // time is a much worse failure than refusing here.
  return dataUrl.length <= MAX_SIGNATURE_DATA_URL_LENGTH ? dataUrl : null;
};
